import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import Reveal from "@/components/places/Reveal";
import CaseAudioPlayer from "@/components/places/CaseAudioPlayer";
import SEO from "@/components/places/SEO";
import VideoEmbed from "@/components/places/VideoEmbed";
import ContentBlocks from "@/components/places/ContentBlocks";
import ClassicBadge from "@/components/places/ClassicBadge";
import { ImplementationWrapper } from "@/components/places/InImplementationBadge";
import { pickLangObj, TRANSLATABLE_FIELDS } from "@/lib/pickLang";
import { Sparkles, ChevronRight, Loader2, Quote as QuoteIcon } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DeliveriesHtml — renders sanitized HTML of the Deliveries field and applies
 * the configured bullet color to every <ul>/<ol> item via a CSS variable.
 */
function DeliveriesHtml({ html, bulletColor }) {
  const style = bulletColor ? { "--case-bullet": bulletColor } : undefined;
  return (
    <div
      className="md:col-span-9 case-rich case-deliveries-rich text-base md:text-lg leading-relaxed text-neutral-800"
      style={style}
      data-testid="case-deliveries-list"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Multi-language block labels for the case narrative sections
const BLOCK_LABELS = {
  pt: { situation: "Situação", solution: "Solução", strategy: "Estratégia", deliveries: "Entregas", results: "Resultados" },
  en: { situation: "Situation", solution: "Solution", strategy: "Strategy", deliveries: "Deliveries", results: "Results" },
  es: { situation: "Situación", solution: "Solución", strategy: "Estrategia", deliveries: "Entregas", results: "Resultados" },
};

const FICHA = {
  pt: { title: "Ficha técnica", empr: "Empreendimento", inc: "Incorporadora", direction: "Direção", type: "Tipo", cat: "Categorias", loc: "Localização", typologies: "Tipologias", sizes: "Metragens", period: "Período", services: "Serviços", summarize: "Resumir este case em 5 pontos", quero: "Quero um projeto assim.", client: "Quero ser cliente", seeAll: "Veja todos os cases" },
  en: { title: "Fact sheet", empr: "Project", inc: "Developer", direction: "Direction", type: "Type", cat: "Categories", loc: "Location", typologies: "Typologies", sizes: "Sizes", period: "Period", services: "Services", summarize: "Summarize this case in 5 points", quero: "I want a project like this.", client: "Become a client", seeAll: "See all cases" },
  es: { title: "Ficha técnica", empr: "Proyecto", inc: "Desarrolladora", direction: "Dirección", type: "Tipo", cat: "Categorías", loc: "Ubicación", typologies: "Tipologías", sizes: "Metrajes", period: "Período", services: "Servicios", summarize: "Resumir este caso en 5 puntos", quero: "Quiero un proyecto así.", client: "Quiero ser cliente", seeAll: "Ver todos los casos" },
};

export default function CaseDetail() {
  const { slug } = useParams();
  const { cases: CASES, cases_config = {} } = useContent();
  const { lang, t } = useLang();
  const L = FICHA[lang] || FICHA.pt;
  const BL = BLOCK_LABELS[lang] || BLOCK_LABELS.pt;

  // Try the in-memory context first — if not found (deep link / refresh before
  // ContentProvider finishes hydrating), fetch the case directly by slug.
  const cCtx = CASES.find((x) => x.slug === slug);
  const [fetched, setFetched] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const cRaw = cCtx || fetched;
  // Overlay translated fields on top of the PT source; falls back to PT for any
  // missing translation. Empty overlay when lang === "pt" or item lacks translations.
  const c = useMemo(() => pickLangObj(cRaw, lang, TRANSLATABLE_FIELDS.case), [cRaw, lang]);

  useEffect(() => {
    if (cCtx || fetched) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/content/cases/${slug}`);
        if (!cancelled) setFetched(data);
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, cCtx, fetched]);  // eslint-disable-line react-hooks/exhaustive-deps

  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [loading, setLoading] = useState(false);

  // Pre-warm the TTS cache in the background: on case page load, kick off a
  // fetch to the audio endpoint so by the time the visitor clicks Play the
  // MP3 is already generated and cached in object storage. Fully silent —
  // no UI feedback, no error if it fails. Must be declared BEFORE any early
  // return to satisfy the Rules of Hooks (React error if placed later).
  useEffect(() => {
    if (!c?.slug) return;
    try {
      fetch(`${API}/ai/tts/case/${c.slug}?lang=${lang}`, { method: "GET", keepalive: true }).catch(() => {});
    } catch {}
  }, [c?.slug, lang]);

  const blocks = useMemo(() => c ? [
    { t: BL.situation, v: c.situation, k: "situation" },
    { t: BL.solution, v: c.solution, k: "solution" },
    { t: BL.strategy, v: c.strategy, k: "strategy" },
    { t: BL.deliveries, v: c.deliveries, k: "deliveries" },
    c.results && { t: BL.results, v: c.results, k: "results" },
  ].filter(Boolean) : [], [c, BL]);

  const sectionStyles = c?.section_styles || {};
  const colorFor = (k) => sectionStyles[`${k}_color`] || null;
  const bulletColor = sectionStyles.deliveries_bullet_color || null;

  // Rich HTML in situation/solution/strategy/deliveries/results is sanitized on render.
  // Legacy plain-text values pass through untouched (no tags to purify).
  const RICH_OPTS = {
    ALLOWED_TAGS: ["p","br","strong","em","u","a","ul","ol","li","span","h2","h3","h4","code"],
    ALLOWED_ATTR: ["href","target","rel","style","class"],
    ALLOWED_STYLES: {
      "*": {
        "font-size": [/^\d+(?:px|em|rem|%)$/],
        "color": [/^#(?:[0-9a-f]{3,8})$/i, /^rgb\(/i, /^rgba\(/i],
        "background-color": [/^#(?:[0-9a-f]{3,8})$/i, /^rgb\(/i, /^rgba\(/i],
        "text-align": [/^(?:left|center|right|justify)$/],
        "font-weight": [/^(?:bold|normal|\d{3})$/],
      },
    },
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|\/)/i,
  };
  const looksLikeHtml = (s) => typeof s === "string" && /<[a-z][\s\S]*>/i.test(s);
  const sanitize = (s) => DOMPurify.sanitize(String(s || ""), RICH_OPTS);
  const stripTags = (s) => String(s || "").replace(/<[^>]*>/g, " ");

  // "Próximos projetos" — next 4 cases in the sequence, wrapping to start if at the end
  const nextProjects = useMemo(() => {
    if (!c || !CASES.length) return [];
    // Never surface hidden cases in the "Continue a jornada" carousel.
    const visible = CASES.filter((x) => !x.is_hidden);
    const idx = visible.findIndex((x) => x.slug === c.slug);
    if (idx < 0) return visible.slice(0, 4);
    const ordered = [...visible.slice(idx + 1), ...visible.slice(0, idx)];
    return ordered.filter((x) => x.slug !== c.slug).slice(0, 4);
  }, [c, CASES]);

  // Only redirect if we've confirmed via API that the case doesn't exist.
  if (notFound) return <Navigate to="/cases" replace />;
  if (!c) {
    return (
      <section className="theme-light min-h-[60vh] pt-40 pb-20 flex items-center justify-center" data-testid="case-detail-loading">
        <div className="animate-pulse text-sm uppercase tracking-widest text-neutral-500">Carregando case…</div>
      </section>
    );
  }

  // Background applied to the "body" content of the case (below the audio player / hero)
  const detailBg = c.background_color || cases_config.detail_bg || "";
  const contentStyle = detailBg ? { backgroundColor: detailBg } : undefined;

  const generateSummary = async () => {
    setLoading(true);
    setSummaryError("");
    try {
      // 30s hard timeout — the LLM call typically finishes in 5-10s, but the
      // cloud proxy may drop long connections. If we hit the timeout we show
      // an actionable error instead of spinning forever.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 30000);
      const { data } = await axios.post(
        `${API}/ai/summarize-case`,
        { title: c.title, situation: c.situation, solution: c.solution, strategy: c.strategy, deliveries: c.deliveries, results: c.results || "" },
        { signal: controller.signal, timeout: 30000 }
      );
      clearTimeout(timer);
      setSummary(data.points || []);
    } catch (e) {
      setSummaryError(e?.name === "CanceledError" || e?.code === "ECONNABORTED"
        ? "A geração demorou mais que o esperado. Tente novamente."
        : "Não conseguimos gerar o resumo agora. Tente em alguns instantes.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <SEO
        title={c.meta_title || `${c.title} — ${c.developer}`}
        description={c.meta_description || c.concept || `${c.title}: case ${c.developer} em ${c.city}/${c.state}. ${(c.results || c.deliveries || "").slice(0, 140)}`}
        image={c.og_image || c.cover}
        type="article"
        focusKeyword={c.main_keyword || c.type}
        keywords={c.secondary_keywords || [c.developer, c.city, ...(c.categories || [])].filter(Boolean)}
        noindex={!!c.noindex}
        article={{
          published: c.published_at || `${c.year}-01-01`,
          modified: c.updated_at || c.published_at,
          author: c.director || "Places",
          section: c.type,
          tags: [c.main_keyword, ...(c.secondary_keywords || []), ...(c.categories || [])].filter(Boolean),
        }}
        breadcrumbs={[
          { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
          { name: "Cases", url: typeof window !== "undefined" ? window.location.origin + "/cases" : "/cases" },
          { name: c.title, url: typeof window !== "undefined" ? window.location.href : `/cases/${c.slug}` },
        ]}
      />
      <section className="theme-light pt-32 pb-8">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-6" data-testid="breadcrumb"><Link to="/" className="hover:text-black">Home</Link><ChevronRight className="w-3 h-3" /><Link to="/cases" className="hover:text-black">Cases</Link><ChevronRight className="w-3 h-3" /><span className="text-black">{c.title}</span></div>
          <Reveal><h1 data-testid="case-hero-title" className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">{c.concept || c.title}</h1><p className="mt-6 text-xs uppercase tracking-widest text-neutral-500">{c.developer} · {c.city}/{c.state} · {c.year}</p></Reveal>
        </div>
      </section>

      <section className="theme-light">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            {c.video_url ? (
              <div className="mb-8"><VideoEmbed src={c.video_url} poster={c.cover} title={c.title} aspect="aspect-[16/9]" /></div>
            ) : (
              <div className="aspect-[16/9] overflow-hidden mb-8"><img src={c.cover} alt={c.cover_alt || c.title} className="w-full h-full object-cover" /></div>
            )}
          </Reveal>
        </div>
      </section>

      <section className="theme-light py-8 md:py-16" style={contentStyle} data-testid="case-body">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <aside className="md:col-span-4 md:sticky md:top-32 md:self-start space-y-4">
            <div className="border border-black/10 p-6" data-testid="case-ficha">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{L.title}</div>
              <dl className="space-y-3 text-sm">
                <div><dt className="text-neutral-500 text-xs">{L.empr}</dt><dd className="font-semibold">{c.title}</dd></div>
                <div><dt className="text-neutral-500 text-xs">{L.inc}</dt><dd>{c.developer}</dd></div>
                {c.director && <div data-testid="case-director"><dt className="text-neutral-500 text-xs">{L.direction}</dt><dd>{c.director}</dd></div>}
                {(c.main_team || []).length > 0 && (
                  <div data-testid="case-main-team">
                    <dt className="text-neutral-500 text-xs">{L.team || "Equipe Principal"}</dt>
                    <dd>
                      <ul className="space-y-0.5 mt-1">
                        {c.main_team.map((p, i) => (
                          <li key={`${p.name}-${i}`} className="text-[13px]"><span className="font-medium">{p.name}</span>{p.role && <span className="text-neutral-500"> — {p.role}</span>}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                {c.type && <div><dt className="text-neutral-500 text-xs">{L.type}</dt><dd>{c.type}</dd></div>}
                <div><dt className="text-neutral-500 text-xs">{L.cat || "Projeto"}</dt><dd>{(c.categories || []).join(", ")}</dd></div>
                {(c.verticals || []).length > 0 && <div><dt className="text-neutral-500 text-xs">Vertical</dt><dd>{(c.verticals || []).join(", ")}</dd></div>}
                {c.padrao && <div data-testid="case-padrao"><dt className="text-neutral-500 text-xs">Padrão</dt><dd>{c.padrao}</dd></div>}
                <div><dt className="text-neutral-500 text-xs">{L.loc}</dt><dd>{c.city}/{c.state}{c.country && c.country !== "Brasil" ? ` · ${c.country}` : ""}</dd></div>
                <div><dt className="text-neutral-500 text-xs">{L.typologies}</dt><dd>{c.typologies}</dd></div>
                <div><dt className="text-neutral-500 text-xs">{L.sizes}</dt><dd>{c.sizes || "—"}</dd></div>
                <div><dt className="text-neutral-500 text-xs">{L.period}</dt><dd>{c.period}</dd></div>
                {c.vgv_estimado && <div data-testid="case-vgv"><dt className="text-neutral-500 text-xs">VGV Estimado</dt><dd className="font-semibold text-accent-orange">{c.vgv_estimado}</dd></div>}
                <div><dt className="text-neutral-500 text-xs">{L.services}</dt><dd className="flex flex-wrap gap-1 mt-1">{(c.services || []).map((s) => <span key={s} className="text-[10px] bg-black text-white px-2 py-0.5">{s}</span>)}</dd></div>
              </dl>
              <button onClick={generateSummary} disabled={loading} data-testid="ai-summary-btn" className="mt-6 w-full btn-sharp bg-black text-white rounded-full justify-center text-xs hover:bg-accent-yellow hover:text-black transition-colors">{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Gerando resumo…</> : <><Sparkles className="w-4 h-4 text-accent-yellow" /> {L.summarize}</>}</button>
              {summaryError && <div className="mt-3 text-xs text-red-500 border border-red-200 bg-red-50 px-3 py-2 rounded" data-testid="ai-summary-error">{summaryError} <button className="underline ml-1" onClick={generateSummary}>Tentar novamente</button></div>}
              {summary && (<ul className="mt-4 space-y-2" data-testid="ai-summary-result">{summary.map((p, i) => (<li key={`pt-${i}-${p.slice(0,10)}`} className="text-xs border-l-2 border-accent-yellow pl-3">{p}</li>))}</ul>)}
            </div>
            {/* Audio player fica horizontalmente alinhado com a ficha/summary — bloco lateral */}
            <CaseAudioPlayer slug={c.slug} defaultLang={lang} />
          </aside>

          <div className="md:col-span-8 space-y-12">
            {blocks.map((b) => {
              const isDeliveries = b.k === "deliveries";
              const titleColor = colorFor(b.k);
              const titleStyle = titleColor ? { color: titleColor } : undefined;
              // Deliveries can be either rich HTML (with <ul><li>) or a plain text with separators
              const isHtml = looksLikeHtml(b.v);
              return (
                <Reveal key={b.t}>
                  <div className="grid md:grid-cols-12 gap-6">
                    <div
                      data-testid={`case-title-${b.k}`}
                      className={`md:col-span-3 text-xs uppercase tracking-widest ${titleColor ? "" : "text-accent-orange"}`}
                      style={titleStyle}
                    >{b.t}</div>
                    {isDeliveries ? (
                      isHtml ? (
                        <DeliveriesHtml html={sanitize(b.v)} bulletColor={bulletColor} />
                      ) : (
                        <ul className="md:col-span-9 space-y-1.5 text-sm md:text-[15px] text-neutral-800" data-testid="case-deliveries-list">
                          {(b.v || "").split(/[\n;,]+|(?<!\d)[\.\u2022](?!\d)/)
                            .map((line) => line.replace(/^[\s\-•·▪.,;]+/, "").trim())
                            .filter(Boolean)
                            .map((line, i) => (
                              <li key={i} className="flex gap-2 leading-snug">
                                <span className={bulletColor ? "" : "text-accent-orange"} style={bulletColor ? { color: bulletColor, marginTop: "0.375rem" } : { marginTop: "0.375rem" }} aria-hidden>•</span>
                                <span>{line}</span>
                              </li>
                            ))}
                        </ul>
                      )
                    ) : (
                      isHtml ? (
                        <div className="md:col-span-9 case-rich text-base md:text-[15px] leading-relaxed text-neutral-800" data-testid={`case-body-${b.k}`}
                          dangerouslySetInnerHTML={{ __html: sanitize(b.v) }} />
                      ) : (
                        <div className="md:col-span-9 text-base md:text-[15px] leading-relaxed text-neutral-800" data-testid={`case-body-${b.k}`}>{b.v}</div>
                      )
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client quote block */}
      {c.quote?.enabled && c.quote?.text && (
        <section className="theme-light py-16" style={contentStyle} data-testid="case-quote">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <QuoteIcon className="w-16 h-16 text-accent-orange mx-auto mb-6 opacity-80" strokeWidth={1.5} />
            <blockquote className="font-display text-2xl md:text-3xl leading-tight text-black">&ldquo;{c.quote.text}&rdquo;</blockquote>
            <div className="mt-8 flex flex-col items-center justify-center gap-3">
              {c.quote.avatar && <img src={c.quote.avatar.startsWith("/api/media/") ? `${process.env.REACT_APP_BACKEND_URL}${c.quote.avatar}` : c.quote.avatar} alt={c.quote.author || ""} className="w-14 h-14 rounded-full object-cover border-2 border-accent-orange" />}
              <div className="text-center">
                <div className="font-semibold text-sm">{c.quote.author}</div>
                {c.quote.role && <div className="text-xs text-neutral-500">{c.quote.role}</div>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Visual content — new ContentBlocks system (preferred). Falls back to legacy gallery. */}
      {(c.content_blocks || []).length > 0 ? (
        <section className="theme-light py-8" style={contentStyle}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <ContentBlocks blocks={c.content_blocks} />
          </div>
        </section>
      ) : c.gallery?.length > 0 && (
        <section className="theme-light py-16 space-y-8" style={contentStyle}>
          {(c.gallery || []).map((g, i) => (
            <Reveal key={`gallery-${c.slug}-${i}`}>
              {/* Full-bleed: each legacy gallery image spans the entire container
                  width edge-to-edge (no side padding), matching the "wide canvas"
                  the client asked for. */}
              <div className="w-full">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={g} alt={c.cover_alt || c.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
              </div>
            </Reveal>
          ))}
        </section>
      )}

      <section className="theme-dark py-24 text-center"><div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal><div className="font-display text-4xl md:text-6xl mb-8">{t.case?.ctaHeadline || L.quero}</div><div className="flex flex-wrap justify-center gap-3"><Link to="/contato?intent=client" className="bg-accent-yellow text-black btn-sharp rounded-full border border-transparent hover:bg-white transition-colors" data-testid="case-cta-client">{t.cta?.client || L.client}</Link><Link to="/cases" className="border border-white/30 btn-sharp rounded-full hover:bg-white hover:text-black transition-colors" data-testid="case-cta-see-all">{t.cta?.allCases || L.seeAll}</Link></div></Reveal>
      </div></section>

      {/* Próximos projetos — mantém a sequência da grid principal */}
      {nextProjects.length > 0 && (
        <section className="theme-light py-20 border-t border-black/10" data-testid="case-next-projects">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-accent-orange mb-2">{t.case?.nextProjectsEyebrow || "Próximos projetos"}</div>
                <h2 className="font-display text-3xl md:text-5xl leading-tight">{t.case?.continueJourneyTitle || "Continue a jornada."}</h2>
              </div>
              <Link to="/cases" className="text-xs uppercase tracking-widest border border-black/15 hover:border-black hover:bg-black hover:text-white transition-colors px-5 py-3 rounded-full" data-testid="case-next-see-all">{t.case?.continueJourneySeeAll || "Ver tudo"}</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {nextProjects.map((n, i) => (
                <Reveal key={n.slug} delay={i * 0.05}>
                  <ImplementationWrapper active={!!n.is_in_implementation} badgeSize="sm">
                    <Link to={`/cases/${n.slug}`} className="group block" data-testid={`next-case-${n.slug}`}>
                      <div className="aspect-[4/3] overflow-hidden bg-neutral-200 relative">
                        <ClassicBadge show={!!n.is_classic} size="sm" />
                        <img src={n.cover} alt={n.title} title={n.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      </div>
                      <div className="pt-3">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{n.developer}</div>
                        <div className="font-display text-lg md:text-xl leading-tight mt-1">{n.title}</div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">{n.city}/{n.state} · {n.year}</div>
                      </div>
                    </Link>
                  </ImplementationWrapper>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
